import type { PrinterProfile, PrinterProfileCreate, PrinterProfileUpdate } from "@/api/client";

export const PAPER_SIZES = ["A3", "A4", "A5", "Letter", "Legal"] as const;
export type PaperSize = (typeof PAPER_SIZES)[number];

const TEXT_KEYS = ["vendor", "model", "ipp_uri", "vendor_specs"] as const;
const OFFSET_KEYS = ["offset_x_mm", "offset_y_mm"] as const;
export const MARGIN_KEYS = ["margin_top_mm", "margin_right_mm", "margin_bottom_mm", "margin_left_mm"] as const;

/** Inputs hold what was typed; numbers are parsed when the body is built. */
export type ProfileFormValues = {
  name: string;
  vendor: string;
  model: string;
  paper_size: PaperSize;
  offset_x_mm: string;
  offset_y_mm: string;
  margin_top_mm: string;
  margin_right_mm: string;
  margin_bottom_mm: string;
  margin_left_mm: string;
  preprinted_stock: boolean;
  default_sub_batch_size: string;
  ipp_uri: string;
  vendor_specs: string;
};
export type ProfileField = keyof ProfileFormValues;
export type ProfileFormErrors = Partial<Record<ProfileField, string>>;

export const EMPTY_PROFILE_FORM: ProfileFormValues = {
  name: "",
  vendor: "",
  model: "",
  paper_size: "A4",
  offset_x_mm: "0",
  offset_y_mm: "0",
  margin_top_mm: "",
  margin_right_mm: "",
  margin_bottom_mm: "",
  margin_left_mm: "",
  preprinted_stock: false,
  default_sub_batch_size: "20",
  ipp_uri: "",
  vendor_specs: "",
};

export function profileFormFrom(profile: PrinterProfile): ProfileFormValues {
  const margin = (v: number | null) => (v === null ? "" : String(v));
  return {
    name: profile.name,
    vendor: profile.vendor ?? "",
    model: profile.model ?? "",
    paper_size: profile.paper_size,
    offset_x_mm: String(profile.offset_x_mm),
    offset_y_mm: String(profile.offset_y_mm),
    margin_top_mm: margin(profile.margin_top_mm),
    margin_right_mm: margin(profile.margin_right_mm),
    margin_bottom_mm: margin(profile.margin_bottom_mm),
    margin_left_mm: margin(profile.margin_left_mm),
    preprinted_stock: profile.preprinted_stock,
    default_sub_batch_size: String(profile.default_sub_batch_size),
    ipp_uri: profile.ipp_uri ?? "",
    vendor_specs: profile.vendor_specs ?? "",
  };
}

// Number("") is 0, so blank must be checked first.
const parse = (value: string) => (value.trim() === "" ? NaN : Number(value));
const marginValue = (value: string) => (value.trim() === "" ? null : Number(value));

export function validateProfileForm(form: ProfileFormValues): ProfileFormErrors {
  const errors: ProfileFormErrors = {};
  if (form.name.trim() === "") errors.name = "is required";
  for (const key of OFFSET_KEYS) {
    const v = parse(form[key]);
    if (!(v >= -50 && v <= 50)) errors[key] = "must be between -50 and 50 mm";
  }
  for (const key of MARGIN_KEYS) {
    const v = marginValue(form[key]);
    if (v !== null && !(v >= 0 && v <= 100)) errors[key] = "must be between 0 and 100 mm, or empty to keep the template's";
  }
  const size = parse(form.default_sub_batch_size);
  if (!(Number.isInteger(size) && size >= 1 && size <= 500)) {
    errors.default_sub_batch_size = "must be a whole number between 1 and 500";
  }
  return errors;
}

export function toCreateBody(form: ProfileFormValues): PrinterProfileCreate {
  const body: PrinterProfileCreate = {
    name: form.name.trim(),
    paper_size: form.paper_size,
    offset_x_mm: parse(form.offset_x_mm),
    offset_y_mm: parse(form.offset_y_mm),
    preprinted_stock: form.preprinted_stock,
    default_sub_batch_size: parse(form.default_sub_batch_size),
  };
  for (const key of TEXT_KEYS) {
    if (form[key].trim() !== "") body[key] = form[key].trim();
  }
  for (const key of MARGIN_KEYS) {
    const v = marginValue(form[key]);
    if (v !== null) body[key] = v;
  }
  return body;
}

/** Only the keys that differ from the saved profile; the API clears a margin on null and text on "". */
export function toUpdateBody(form: ProfileFormValues, profile: PrinterProfile): PrinterProfileUpdate {
  const body: PrinterProfileUpdate = {};
  if (form.name.trim() !== profile.name) body.name = form.name.trim();
  if (form.paper_size !== profile.paper_size) body.paper_size = form.paper_size;
  if (form.preprinted_stock !== profile.preprinted_stock) body.preprinted_stock = form.preprinted_stock;
  for (const key of OFFSET_KEYS) {
    if (parse(form[key]) !== profile[key]) body[key] = parse(form[key]);
  }
  const size = parse(form.default_sub_batch_size);
  if (size !== profile.default_sub_batch_size) body.default_sub_batch_size = size;
  for (const key of MARGIN_KEYS) {
    const v = marginValue(form[key]);
    if (v !== profile[key]) body[key] = v;
  }
  for (const key of TEXT_KEYS) {
    const v = form[key].trim();
    if (v !== (profile[key] ?? "")) body[key] = v;
  }
  return body;
}
